import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Medal, Star } from "@phosphor-icons/react";

const GRADE_COLORS = {
    A: "bg-emerald-100 text-emerald-800 border-emerald-300",
    B: "bg-blue-100 text-blue-800 border-blue-300",
    C: "bg-amber-100 text-amber-800 border-amber-300",
    D: "bg-orange-100 text-orange-800 border-orange-300",
    E: "bg-red-100 text-red-800 border-red-300",
};

export default function DriverScores() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/driver-scores").then((r) => { setList(r.data); setLoading(false); });
    }, []);

    return (
        <div className="space-y-5" data-testid="driver-scores-page">
            <div>
                <p className="prc-label-overline">ドライバー評価スコア</p>
                <h1 className="text-[1.685rem] font-bold tracking-tight">運用品質ランキング</h1>
                <p className="text-sm text-[var(--prc-muted)] mt-1">
                    直近90日間の<strong className="text-[var(--prc-ink)]">使用前点検</strong>と<strong className="text-[var(--prc-ink)]">運行記録</strong>を統合して総合スコアを算出します（点検品質 60% + 安全運転 40%）。
                </p>
            </div>

            {loading ? <p className="prc-label-overline" data-testid="ds-loading">集計中…</p> : list.length === 0 ? (
                <p className="prc-card p-8 text-sm text-[var(--prc-muted)] text-center" data-testid="ds-empty">
                    直近90日間に点検データがありません。使用前点検を実施するとスコアが表示されます。
                </p>
            ) : (
                <>
                    {/* Top 3 podium */}
                    <div className="grid md:grid-cols-3 gap-4" data-testid="ds-podium">
                        {list.slice(0, 3).map((d, i) => (
                            <div key={d.inspector_id} className={`prc-card p-5 relative ${i === 0 ? "ring-2 ring-amber-400" : ""}`} data-testid={`ds-top-${i + 1}`}>
                                <div className="flex items-center gap-2">
                                    {i === 0 && <Trophy size={24} className="text-amber-500" weight="fill" />}
                                    {i === 1 && <Medal size={24} className="text-slate-400" weight="fill" />}
                                    {i === 2 && <Medal size={24} className="text-amber-700" weight="fill" />}
                                    <p className="prc-label-overline">第 {i + 1} 位</p>
                                </div>
                                <h3 className="mt-3 text-lg font-bold">{d.name}</h3>
                                <div className="mt-3 flex items-baseline gap-3">
                                    <p className="prc-metric">{d.score}</p>
                                    <span className={`prc-badge border text-base ${GRADE_COLORS[d.grade]}`}>{d.grade}</span>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-[10px] font-mono uppercase tracking-wider text-[var(--prc-muted)]">
                                    <div>
                                        <p>// 点検</p>
                                        <p className="text-[var(--prc-ink)] normal-case">{d.total_inspections}件 · OK {d.ok_rate}%</p>
                                    </div>
                                    <div>
                                        <p>// 運行</p>
                                        <p className="text-[var(--prc-ink)] normal-case">
                                            {d.trips}回 · {(d.total_km || 0).toLocaleString()}km
                                            {d.speeding_trips > 0 && <span className="text-rose-300"> · 超過{d.speeding_trips}</span>}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="prc-card overflow-x-auto">
                        <table className="prc-table" data-testid="ds-table">
                            <thead>
                                <tr>
                                    <th rowSpan={2}>順位</th>
                                    <th rowSpan={2}>ドライバー</th>
                                    <th rowSpan={2} className="text-right">総合<br />スコア</th>
                                    <th rowSpan={2}>評価</th>
                                    <th colSpan={4} className="text-center border-l border-[var(--prc-border)]">点検品質</th>
                                    <th colSpan={5} className="text-center border-l border-[var(--prc-border)]">安全運転</th>
                                </tr>
                                <tr>
                                    <th className="text-right border-l border-[var(--prc-border)]">点検数</th>
                                    <th className="text-right">完了率</th>
                                    <th className="text-right">OK率</th>
                                    <th className="text-right">NG</th>
                                    <th className="text-right border-l border-[var(--prc-border)]">運行<br />回数</th>
                                    <th className="text-right">走行<br />km</th>
                                    <th className="text-right">速度<br />超過</th>
                                    <th className="text-right">アイ<br />ドル</th>
                                    <th className="text-right">燃費<br />km/L</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((d, i) => (
                                    <tr key={d.inspector_id} data-testid={`ds-row-${d.inspector_id}`}>
                                        <td className="num font-mono">{i + 1}</td>
                                        <td className="font-semibold">{d.name}</td>
                                        <td className="num font-mono font-bold text-[var(--prc-green)]">{d.score}</td>
                                        <td><span className={`prc-badge border ${GRADE_COLORS[d.grade]}`}>{d.grade}</span></td>
                                        {/* Inspection */}
                                        <td className="num border-l border-[var(--prc-border)]">{d.total_inspections}</td>
                                        <td className="num">{d.total_inspections > 0 ? `${d.completion_rate}%` : "—"}</td>
                                        <td className="num">{d.total_inspections > 0 ? `${d.ok_rate}%` : "—"}</td>
                                        <td className={`num ${d.ng_count > 0 ? "text-rose-300 font-semibold" : ""}`}>{d.ng_count || 0}</td>
                                        {/* Safety */}
                                        <td className="num border-l border-[var(--prc-border)]">{d.trips || 0}</td>
                                        <td className="num">{d.trips > 0 ? d.total_km.toLocaleString() : "—"}</td>
                                        <td className={`num ${d.speeding_trips > 0 ? "text-rose-300 font-semibold" : ""}`}>
                                            {d.trips > 0 ? `${d.speeding_trips} (${d.speeding_rate}%)` : "—"}
                                        </td>
                                        <td className={`num ${d.idling_trips > 0 ? "text-amber-300" : ""}`}>
                                            {d.trips > 0 ? d.idling_trips : "—"}
                                        </td>
                                        <td className="num">
                                            {d.fuel_efficiency_kmpl != null
                                                ? <span className={d.fuel_efficiency_kmpl >= 12 ? "text-[var(--prc-green)]" : ""}>{d.fuel_efficiency_kmpl}</span>
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="prc-card p-5">
                            <p className="prc-label-overline mb-2">点検品質スコア (60%)</p>
                            <ul className="text-sm space-y-1 text-[var(--prc-muted)]">
                                <li>• <strong className="text-[var(--prc-ink)]">完了率 (45%)</strong>：開始した点検を完了させた割合</li>
                                <li>• <strong className="text-[var(--prc-ink)]">OK率 (30%)</strong>：完了点検のうち全項目OKだった割合</li>
                                <li>• <strong className="text-[var(--prc-ink)]">署名率 (20%)</strong>：署名を残して点検を確定した割合</li>
                                <li>• <strong className="text-[var(--prc-ink)]">NGペナルティ</strong>：NG発生件数 × 30 / 総点検数</li>
                            </ul>
                        </div>
                        <div className="prc-card p-5">
                            <p className="prc-label-overline mb-2">安全運転スコア (40%)</p>
                            <ul className="text-sm space-y-1 text-[var(--prc-muted)]">
                                <li>• <strong className="text-rose-300">速度超過ペナルティ</strong>：(走行 km ÷ 時間) &gt; 60 km/h の運行を超過扱い、率×50pt 減点</li>
                                <li>• <strong className="text-amber-300">アイドリングペナルティ</strong>：30分以上の運行で距離0kmは率×30pt 減点</li>
                                <li>• <strong className="text-[var(--prc-green)]">燃費ボーナス</strong>：12 km/L 以上で 最大 +5pt</li>
                                <li className="text-[10px] mt-2 italic">※ 運行記録に開始/終了時刻と オドメータ・給油量を入力するほど精度が向上します</li>
                            </ul>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
